import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "@/lib/api";

const ballLabel = (b) => (b.is_wicket ? "W" : b.is_wide ? "wd" : b.is_no_ball ? "nb" : b.is_six ? "6" : b.is_four ? "4" : String(b.runs ?? 0));
const ballCls = (b) => (b.is_wicket ? "bg-cr-danger text-white" : b.is_six ? "bg-cr-gold text-black" : b.is_four ? "bg-emerald-500 text-black" : b.is_wide || b.is_no_ball ? "bg-sky-500/30 text-sky-200" : "bg-cr-elevated text-cr-ink");
const say = (b) => (b.is_wicket ? `OUT! ${b.batsman_name} is gone.` : b.is_six ? `SIX! ${b.batsman_name} clears the rope.` : b.is_four ? `FOUR! ${b.batsman_name} finds the boundary.`
  : b.is_wide ? "Wide ball." : b.is_no_ball ? "No ball!" : b.runs ? `${b.runs} run${b.runs > 1 ? "s" : ""} taken.` : "Dot ball.");

const Commentary = ({ rows, ext }) => (
  <div data-testid="live-commentary" className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
    {rows.length === 0 && <p className="text-sm text-cr-muted">{ext ? "Ball-by-ball commentary is not available for this match. Score updates are shown above." : "Commentary starts when the first ball is bowled."}</p>}
    {rows.map((b) => (
      <div key={b.id} className="flex items-start gap-3 rounded-lg border border-cr-line bg-cr-surface p-3">
        <span className="w-10 shrink-0 pt-1 font-mono text-xs text-cr-muted">{b.ball_number}</span>
        <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-black ${ballCls(b)}`}>{ballLabel(b)}</span>
        <div className="text-sm"><p className="font-semibold">{b.bowler_name} to {b.batsman_name}</p><p className="text-cr-ink/75">{say(b)}</p></div>
      </div>))}
  </div>
);

const OversChart = ({ innings }) => {
  if (!innings.length) return <p className="text-sm text-cr-muted">Over-by-over chart appears once play starts.</p>;
  const max = Math.max(...innings.map((i) => i.overs.length));
  const data = Array.from({ length: max }, (_, k) => Object.fromEntries([["over", k + 1], ...innings.slice(0, 2).map((i, n) => [`t${n}`, i.overs[k]?.runs ?? null])]));
  return (
    <div data-testid="overs-chart" className="h-80 w-full">
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: -20 }}>
          <XAxis dataKey="over" stroke="#8a8a8a" fontSize={11} /><YAxis stroke="#8a8a8a" fontSize={11} />
          <Tooltip contentStyle={{ background: "#141515", border: "1px solid #2a2a2a" }} />
          <Legend formatter={(v) => innings[Number(v[1])]?.team} />
          <Bar dataKey="t0" fill="#f5c542" radius={[3, 3, 0, 0]} /><Bar dataKey="t1" fill="#38bdf8" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const Table = ({ head, rows }) => (
  <div className="overflow-x-auto"><table className="w-full min-w-[520px] text-sm">
    <thead className="text-left text-xs uppercase text-cr-muted"><tr>{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-cr-line">{r.map((c, j) => <td key={j} className={`px-3 py-2 ${j === 0 ? "font-semibold" : ""}`}>{c}</td>)}</tr>)}</tbody>
  </table></div>
);

const Scorecard = ({ batting, bowling }) => {
  const teams = [...new Set(batting.map((b) => b.team_name))];
  if (!teams.length) return <p className="text-sm text-cr-muted">Scorecard appears once play starts.</p>;
  return (
    <div data-testid="scorecard" className="space-y-6">
      {teams.map((t) => (
        <div key={t} className="card-cr p-4">
          <p className="mb-2 font-display font-bold text-cr-gold">{t} batting</p>
          <Table head={["Batter", "R", "B", "4s", "6s", "SR"]} rows={batting.filter((b) => b.team_name === t).map((b) => [`${b.player_name}${b.is_out ? "" : " *"}`, b.score, b.balls, b.fours, b.sixes, b.strike_rate])} />
          <p className="mb-2 mt-4 font-display font-bold text-cr-gold">Bowling vs {t}</p>
          <Table head={["Bowler", "O", "M", "R", "W", "Wd"]} rows={bowling.filter((b) => b.team_name !== t).map((b) => [b.player_name, b.overs, b.maidens, b.runs, b.wickets, b.wides])} />
        </div>))}
    </div>
  );
};

export default function MatchLive({ id, live, ext }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("commentary");
  useEffect(() => {
    const load = () => api.get(`/cricket/matches/${id}/live`).then((r) => setD(r.data)).catch(() => setD({ commentary: [], innings: [], batting: [], bowling: [] }));
    load();
    const t = live ? setInterval(load, 20000) : null;
    return () => { if (t) clearInterval(t); };
  }, [id, live]);
  return (
    <section className="mt-8">
      <div className="mb-4 flex gap-6 border-b border-cr-line">
        {[["commentary", "Ball by ball"], ["overs", "Overs chart"], ["scorecard", "Scorecard"]].map(([k, l]) => (
          <button key={k} data-testid={`live-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 pb-3 text-sm font-semibold ${tab === k ? "border-cr-gold text-cr-gold" : "border-transparent text-cr-ink/75"}`}>{l}</button>))}
      </div>
      {!d ? <p className="text-sm text-cr-muted">Loading…</p> : tab === "commentary" ? <Commentary rows={d.commentary} ext={ext} /> : tab === "overs" ? <OversChart innings={d.innings} /> : <Scorecard batting={d.batting} bowling={d.bowling} />}
    </section>
  );
}
